import { useNavigate } from "react-router";
import { Ban, CheckCircle2, Mail, MapPin, MoreVertical, Phone, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { idadeEmAnos } from "@/lib/nascimento";
import { getPatientColor, patientGradient } from "@/lib/patient-color";
import { rotuloDeSexo } from "@/lib/patient-sex";
import { buildWhatsAppUrl, WhatsAppIcon } from "@/lib/whatsapp";
import { formatPhone, type PatientRowShape } from "./PatientListRow";

/**
 * O paciente como cartão, pra visão em grade da lista.
 *
 * Duas decisões que a primeira versão errava:
 *
 * 1. **Altura igual.** Antes as seções eram condicionais e o cartão de quem não tem telefone nem
 *    tag encolhia à metade — a grade ficava esfarrapada. Agora a estrutura é sempre a mesma e o
 *    rodapé é empurrado com `mt-auto`; sem contato, o cartão DIZ que não há, que é informação útil
 *    ("falta cadastrar") em vez de um buraco.
 * 2. **Deitado, não empilhado.** Com dois por linha o cartão tem ~600px: identificação à esquerda,
 *    situação e ações à direita. Empilhar tudo no topo deixava metade do cartão vazia.
 *
 * Deliberadamente sem "diagnóstico" nem "última visita": o primeiro não existe como dado (as abas
 * clínicas ainda são placeholder) e o segundo não vem na listagem. Campo vazio fixo ensina o
 * usuário a ignorar o cartão.
 */
export function PatientGridCard({
  patient: p,
  onRemove,
  onToggleActive,
}: {
  patient: PatientRowShape;
  onRemove: (p: PatientRowShape) => void;
  onToggleActive: (p: PatientRowShape) => void;
}) {
  const navigate = useNavigate();
  const anos = idadeEmAnos(p.birthDate);
  const tags = (p.tags ?? []) as string[];
  const local = [p.city, p.state].filter(Boolean).join("/");

  const identificacao = [
    p.identifier && `#${p.identifier}`,
    anos !== null && `${anos} anos`,
    rotuloDeSexo(p.sex),
  ].filter(Boolean) as string[];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/pacientes/${p.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/pacientes/${p.id}`);
        }
      }}
      aria-label={`Abrir ficha de ${p.name}`}
      className={cn(
        // Altura FIXA, não `min-h`: a grade tinha cartões de 90 e de 150px lado a lado conforme o
        // paciente tivesse ou não contato, e o desalinhamento é o que fazia a visão parecer
        // quebrada. `overflow-hidden` garante que conteúdo excepcional corte em vez de esticar.
        "flex h-[152px] cursor-pointer items-stretch gap-4 overflow-hidden rounded-xl border border-zinc-200 bg-white p-4 transition-all hover:border-zinc-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-border dark:bg-card",
        !p.active && "opacity-70",
      )}
    >
      {/* IDENTIFICAÇÃO */}
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {p.avatarUrl ? (
          <img src={p.avatarUrl} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
        ) : (
          <span
            className="flex h-12 w-12 shrink-0 select-none items-center justify-center rounded-full text-base font-bold text-white"
            style={{ background: patientGradient(getPatientColor(p.name)) }}
            aria-hidden
          >
            {p.name.trim().charAt(0).toUpperCase()}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold leading-snug text-zinc-900 dark:text-foreground">
            {p.name}
          </p>
          <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">
            {identificacao.length > 0 ? identificacao.join(" · ") : "Sem dados de identificação"}
          </p>
          {local && (
            <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-zinc-500 dark:text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              {local}
            </p>
          )}

          {tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {tags.slice(0, 3).map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600 dark:bg-muted dark:text-muted-foreground"
                >
                  {t}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="text-[10px] font-medium text-zinc-400 dark:text-muted-foreground">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* CONTATO E SITUAÇÃO — coluna própria: com dois cartões por linha sobra largura, e empilhar
          tudo à esquerda deixava metade do cartão vazia. Mesma leitura em faixas do cartão da
          consulta no Início. */}
      <div className="flex w-[220px] shrink-0 flex-col gap-2 border-l border-zinc-100 pl-4 dark:border-border/60">
        <div className="flex items-center justify-end gap-1">
          {/* Só a exceção ganha selo: "Ativo" em todo cartão não dizia nada. */}
          {!p.active && (
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500 dark:bg-muted dark:text-muted-foreground">
              Inativo
            </span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                aria-label={`Ações de ${p.name}`}
                className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
              className="border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card"
            >
              <DropdownMenuItem onClick={() => onToggleActive(p)}>
                {p.active ? (
                  <>
                    <Ban className="mr-2 h-3.5 w-3.5" /> Inativar
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-3.5 w-3.5" /> Reativar
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onRemove(p)}
                className="text-red-600 focus:text-red-600 dark:text-red-400"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" /> Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="min-w-0 space-y-1 text-xs text-zinc-600 dark:text-muted-foreground">
          {/* Sem contato o cartão DIZ que falta — é informação acionável, e mantém a altura. */}
          {!p.phone && !p.email && (
            <span className="italic text-zinc-400 dark:text-muted-foreground">Sem contato cadastrado</span>
          )}
          {p.phone && (
            <span className="flex items-center gap-1.5">
              <Phone className="h-3 w-3 shrink-0 text-zinc-400" />
              <span className="tabular-nums">{formatPhone(p.phone)}</span>
            </span>
          )}
          {p.email && (
            <span className="flex min-w-0 items-center gap-1.5" title={p.email}>
              <Mail className="h-3 w-3 shrink-0 text-zinc-400" />
              <span className="truncate">{p.email}</span>
            </span>
          )}
        </div>

        {/* O botão existe SEMPRE — some só em alguns cartões deixava a grade irregular e escondia
            que dá pra falar com o paciente por ali. Sem telefone ele fica desabilitado e explica o
            porquê. É `<span>`, não `<button disabled>`: botão desabilitado não dispara os eventos
            de mouse em vários navegadores, então o `title` nunca apareceria. */}
        {p.phone ? (
          <a
            href={buildWhatsAppUrl(p.phone, p.name)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Falar com ${p.name} no WhatsApp`}
            className="mt-auto inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-emerald-200 px-2.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10"
          >
            <WhatsAppIcon className="h-3.5 w-3.5" />
            WhatsApp
          </a>
        ) : (
          <span
            role="button"
            aria-disabled="true"
            title="Cadastre um telefone para utilizar o WhatsApp"
            aria-label="WhatsApp indisponível — cadastre um telefone para utilizar o WhatsApp"
            onClick={(e) => e.stopPropagation()}
            className="mt-auto inline-flex h-8 w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 text-xs font-semibold text-zinc-400 dark:border-border dark:bg-muted/40 dark:text-muted-foreground"
          >
            <WhatsAppIcon className="h-3.5 w-3.5" />
            WhatsApp indisponível
          </span>
        )}
      </div>
    </div>
  );
}
